import type { MouseEvent } from 'react';
import type { Nullable } from 'corgi-types';

import { EventBusEvent } from 'common/enums/EventBus';
import { TrackingEventElement } from 'common/enums/Reclamation';
import type { EventBus } from 'common/event-bus/EventBus';
import { useBackNavigation } from 'common/hooks/back-navigation';
import { useKeyboardClose } from 'common/hooks/keyboard';
import { Element } from 'common/types/tracking';
import type { UseFormSubmitHandlerParams } from '../hooks/useFormSubmitHandler';
import { useFormSubmitHandler } from '../hooks/useFormSubmitHandler';
import type { CloseEventOptions } from '../utils/tracking';
import { dispatchCloseDrawerEvent, dispatchGoBackButtonEvent } from '../utils/tracking';

interface UseDrawerNavigationHandlerParams extends UseFormSubmitHandlerParams {
  eventBus: EventBus;
  resetActiveStep: () => void;
  navigateToPreviousStep: () => boolean;
}

export const useDrawerNavigationHandler = ({
  state,
  eventBus,
  activeStep,
  setAlert,
  resetActiveStep,
  navigateToPreviousStep,
  ...restProps
}: UseDrawerNavigationHandlerParams) => {
  const closeDrawer = (_?: Nullable<Event>, options?: CloseEventOptions) => {
    if (state?.trackingEventType && options) {
      dispatchCloseDrawerEvent({
        type: state.trackingEventType,
        title: activeStep.type,
        options,
      });
    }

    resetActiveStep();
    eventBus.emit(EventBusEvent.ReclamationDrawerToggle, { isOpen: false });
  };

  const { onManualClose } = useBackNavigation({
    isOpen: state?.isOpen ?? false,
    onClose: () => closeDrawer(null, { element: Element.BrowserBack }),
  });

  const drawerCloseHandler = (event?: Nullable<Event>, options?: CloseEventOptions) => {
    onManualClose();
    closeDrawer(event, options);
  };

  useKeyboardClose(state?.isOpen ?? false, () =>
    drawerCloseHandler(null, { element: Element.Escape })
  );

  const backdropClickHandler = () =>
    drawerCloseHandler(null, { element: TrackingEventElement.BackdropDrawer });

  const headerCloseClickHandler = () =>
    drawerCloseHandler(null, { element: TrackingEventElement.Button });

  const goBackClickHandler = (event: MouseEvent) => {
    event.preventDefault();
    setAlert(null);

    if (state?.trackingEventType) {
      dispatchGoBackButtonEvent(state.trackingEventType, activeStep.type);
    }

    if (navigateToPreviousStep()) {
      drawerCloseHandler();
    }
  };

  const submitHandler = useFormSubmitHandler({
    state,
    activeStep,
    setAlert,
    ...restProps,
  });

  return {
    drawerCloseHandler,
    backdropClickHandler,
    headerCloseClickHandler,
    goBackClickHandler,
    submitHandler,
  };
};
